// Base API configuration and utilities
import { API_CONFIG, STORAGE_KEYS, HTTP_STATUS, getTenantDomain, BASE_PATH } from '../utils/constants';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

// Decode base64url (JWT uses base64url, not standard base64)
const base64UrlDecode = (str) => {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  return atob(base64);
};

// Check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const tokenPayload = JSON.parse(base64UrlDecode(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return typeof tokenPayload.exp === 'number' && tokenPayload.exp < currentTime;
  } catch (error) {
    console.error('Token validation error:', error);
    return true;
  }
};

// Handle unauthorized responses
const handleUnauthorized = () => {
  // Clear session data but preserve remembered credentials
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  // Note: Don't remove REMEMBER_CREDENTIALS to preserve "Remember Password" functionality

  // Redirect to login if not already there
  if (window.location.pathname !== `${BASE_PATH}/login`) {
    window.location.href = `${BASE_PATH}/login`;
  }
};

// Base API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;

  const defaultHeaders = {};

  // Only add Content-Type if not FormData (FormData sets its own Content-Type with boundary)
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Add tenant domain header for multi-tenancy
  const tenantDomain = getTenantDomain();
  if (tenantDomain) {
    defaultHeaders['X-Tenant-Domain'] = tenantDomain;
  }

  // Add auth token if available
  const token = getAuthToken();
  const isAuthEndpoint = endpoint.includes('/auth/');
  const isRefreshEndpoint = endpoint.includes('/auth/refresh');
  if (token) {
    if (isTokenExpired(token)) {
      if (isRefreshEndpoint) {
        // Refresh endpoint needs the token even if expired (server validates)
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      } else if (isAuthEndpoint) {
        // For other auth endpoints (login, check-mobile, etc.), silently clear
        // the stale token and proceed without it
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
      } else {
        handleUnauthorized();
        throw new Error('Session expired. Please login again.');
      }
    } else {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Try to parse JSON response
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // Server returned non-JSON (e.g., PHP error page, HTML 500 error)
      if (response.status >= 500) {
        throw new Error('Server error. Please ensure the database is running and try again.');
      }
      throw new Error('Server returned an unexpected response. Please try again.');
    }

    // Handle unauthorized responses (but not for login or refresh endpoints)
    if (
      response.status === HTTP_STATUS.UNAUTHORIZED &&
      !endpoint.includes('/auth/login') &&
      !endpoint.includes('/auth/refresh')
    ) {
      handleUnauthorized();
      throw new Error('Session expired. Please login again.');
    }

    // Handle 403 - distinguish subscription expired from access denied
    if (response.status === HTTP_STATUS.FORBIDDEN && !endpoint.includes('/auth/login')) {
      if (data.message === 'SUBSCRIPTION_EXPIRED') {
        // Don't redirect to login - let SubscriptionContext handle it
        const subError = new Error('SUBSCRIPTION_EXPIRED');
        subError.isSubscriptionExpired = true;
        throw subError;
      }
      // Don't call handleUnauthorized() for 403 responses.
      // 403 means authorization failure (wrong role, missing module permission,
      // deactivated account) — NOT expired session. Callers handle this
      // via their own try/catch (e.g. AuthContext.validateSession clears
      // the session when /auth/verify returns 403 for deactivated accounts).
      throw new Error(data.message || 'Access denied.');
    }

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and ensure the server is running.');
    }
    throw error;
  }
};

// Download helper for CSV/Excel
const apiDownload = async (endpoint, filenameHint = 'report.csv') => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const headers = {};
  const tenantDomainDl = getTenantDomain();
  if (tenantDomainDl) {
    headers['X-Tenant-Domain'] = tenantDomainDl;
  }
  const token = getAuthToken();
  if (token) {
    if (isTokenExpired(token)) {
      handleUnauthorized();
      throw new Error('Session expired. Please login again.');
    }
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, { headers });
  if (response.status === HTTP_STATUS.UNAUTHORIZED || response.status === HTTP_STATUS.FORBIDDEN) {
    handleUnauthorized();
    throw new Error('Session expired. Please login again.');
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP error! status: ${response.status}`);
  }
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = filenameHint;
  if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    if (match && match[1]) filename = match[1];
  }
  const link = document.createElement('a');
  const blobUrl = window.URL.createObjectURL(blob);
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
  return { success: true };
};

// HTTP methods
export const api = {
  get: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  put: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  delete: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'DELETE' }),

  // Dedicated method for FormData uploads
  postFormData: (endpoint, formData, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    }),

  putFormData: (endpoint, formData, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: formData,
    }),

  download: (endpointWithQuery, filenameHint) => apiDownload(endpointWithQuery, filenameHint),
};

export { API_CONFIG };
